import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, Send, FileText, Target, CheckCircle2,
  Circle, PenTool, DollarSign, MapPin, Package, Plus, Handshake, Info,
  Download, CreditCard, ExternalLink, Video, VideoOff, Paperclip, X, Image as ImageIcon, Globe,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, apiUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useSearchParams } from "react-router-dom";

const STAGE_VALUES = [
  "contacto", "evaluacion", "confidencialidad", "propuesta",
  "negociacion", "acuerdo", "ejecucion", "reporte", "cierre",
];

const stageLabel = (t, value) => t(`room.stage_${value}`);

export default function RoomDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : i18n.language === "pt" ? "pt-BR" : "es-ES";
  const [params, setParams] = useSearchParams();
  const [room, setRoom] = useState(null);
  const [text, setText] = useState("");
  const [typed, setTyped] = useState("");
  const [agreementText, setAgreementText] = useState("");
  const [agreementAmount, setAgreementAmount] = useState("");
  const [msDialog, setMsDialog] = useState(false);
  const [ms, setMs] = useState({ title: "", due_date: "", amount: "", currency: "USD" });
  const [payingMsId, setPayingMsId] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const listRef = useRef();

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/api/rooms/${id}`);
      setRoom(data);
      setTimeout(() => listRef.current?.scrollTo({ top: 999999 }), 200);
    } catch (e) {
      toast.error(t("room.not_found"));
      nav("/dashboard/negociaciones");
    }
  }, [id, t, nav]);
  useEffect(() => { load(); }, [load]);

  // Handle Stripe return: poll session status
  const sessionId = params.get("session_id");
  const paymentStatus = params.get("payment");
  useEffect(() => {
    if (paymentStatus === "cancel") {
      toast.info(t("room.payment_cancelled"));
      const p = new URLSearchParams(params); p.delete("payment"); p.delete("session_id"); setParams(p, { replace: true });
      return;
    }
    if (paymentStatus !== "success" || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/api/payments/checkout/status/${sessionId}`);
        if (data.status === "paid") {
          toast.success(t("room.payment_confirmed", { currency: data.currency, amount: data.amount.toLocaleString() }));
          const p = new URLSearchParams(params); p.delete("payment"); p.delete("session_id"); setParams(p, { replace: true });
          load();
          return;
        }
      } catch (e) {
        console.error("payment_status_poll_failed", e);
      }
      if (attempts < 6) setTimeout(poll, 2000);
      else {
        toast.info(t("room.payment_verifying"));
        const p = new URLSearchParams(params); p.delete("payment"); p.delete("session_id"); setParams(p, { replace: true });
      }
    };
    poll();
  }, [sessionId, paymentStatus, load, params, setParams, t]);

  if (!room) return <DashboardLayout title={t("room.title")}><div className="text-slate-500">{t("room.loading")}</div></DashboardLayout>;

  const iSigned = room.nda?.signatures?.some((s) => s.user_id === user.id);
  const allSignedNDA = room.participants.every((p) => room.nda?.signatures?.some((s) => s.user_id === p));
  const iSignedAgreement = room.agreement?.signatures?.some((s) => s.user_id === user.id);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() && pendingAttachments.length === 0) return;
    if (!allSignedNDA) return toast.error(t("room.nda_sign_first"));
    try {
      const body = text.trim() || (pendingAttachments.length > 0 ? `📎 ${t("room.attachment")}` : "");
      const { data } = await api.post(`/api/rooms/${id}/message`, {
        body,
        attachments: pendingAttachments,
      });
      setRoom({ ...room, messages: [...(room.messages || []), data] });
      setText("");
      setPendingAttachments([]);
      setTimeout(() => listRef.current?.scrollTo({ top: 999999 }), 100);
    } catch (e) { toast.error(t("room.send_failed")); }
  };

  const handleFileSelect = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/api/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPendingAttachments((prev) => [...prev, {
          id: data.id, url: data.url, name: data.original_filename,
          content_type: data.content_type, size: data.size,
        }]);
      }
    } catch (e) {
      toast.error(t("room.attach_file_error"));
    }
    setUploading(false);
    ev.target.value = "";
  };

  const removePending = (fid) => setPendingAttachments((prev) => prev.filter((a) => a.id !== fid));

  const toggleShare = async (value) => {
    try {
      await api.post(`/api/rooms/${id}/share-publicly`, { share_publicly: value });
      setRoom({ ...room, share_publicly: value });
      toast.success(value ? t("room.share_toggle_on") : t("room.share_toggle_off"));
    } catch (e) { toast.error(t("room.share_toggle_error")); }
  };

  const signNda = async () => {
    if (!typed.trim()) return toast.error(t("room.nda_type_error"));
    try {
      await api.post(`/api/rooms/${id}/nda/sign`, { typed_name: typed.trim() });
      toast.success(t("room.nda_signed_toast"));
      setTyped("");
      load();
    } catch (e) { toast.error(t("room.nda_sign_error")); }
  };

  const changeStage = async (stage) => {
    await api.post(`/api/rooms/${id}/stage`, { stage });
    load();
  };

  const addMilestone = async () => {
    if (!ms.title.trim()) return toast.error(t("room.milestone_title_required"));
    await api.post(`/api/rooms/${id}/milestone`, {
      title: ms.title,
      due_date: ms.due_date || null,
      amount: ms.amount ? parseFloat(ms.amount) : null,
      currency: ms.currency || "USD",
    });
    setMsDialog(false);
    setMs({ title: "", due_date: "", amount: "", currency: "USD" });
    load();
  };

  const completeMilestone = async (msId) => {
    await api.post(`/api/rooms/${id}/milestone/${msId}/complete`);
    load();
  };

  const payMilestone = async (msId) => {
    setPayingMsId(msId);
    try {
      const { data } = await api.post("/api/payments/checkout/milestone", {
        room_id: id, milestone_id: msId,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(t("room.milestone_pay_error"));
      setPayingMsId(null);
    }
  };

  const createAgreement = async () => {
    if (!agreementText.trim()) return toast.error(t("room.agreement_text_required"));
    await api.post(`/api/rooms/${id}/agreement`, {
      text: agreementText,
      amount: agreementAmount ? parseFloat(agreementAmount) : null,
      currency: "USD",
    });
    setAgreementText("");
    setAgreementAmount("");
    load();
    toast.success(t("room.agreement_created_toast"));
  };

  const signAgreement = async () => {
    if (!typed.trim()) return toast.error(t("room.nda_type_error"));
    await api.post(`/api/rooms/${id}/agreement/sign`, { typed_name: typed.trim() });
    setTyped("");
    load();
    toast.success(t("room.agreement_signed_toast"));
  };

  return (
    <DashboardLayout
      title={room.title}
      subtitle={t("room.subtitle_default", { name: room.counterparty?.name || t("room.counterparty_default"), stage: stageLabel(t, room.stage) })}
      actions={
        <Button variant="outline" onClick={() => nav("/dashboard/negociaciones")} className="rounded-full">
          <ArrowLeft size={14} className="mr-2" /> {t("room.back_rooms")}
        </Button>
      }
    >
      {/* Timeline */}
      <div className="kpi-card p-5 mb-6 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {STAGE_VALUES.map((value, i) => {
            const currentIdx = STAGE_VALUES.findIndex((x) => x === room.stage);
            const done = i <= currentIdx;
            const isCurrent = i === currentIdx;
            return (
              <React.Fragment key={value}>
                <button
                  onClick={() => changeStage(value)}
                  data-testid={`stage-${value}`}
                  className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg transition-colors ${isCurrent ? "bg-gold/15" : "hover:bg-slate-50"}`}
                >
                  <span className={`inline-flex h-8 w-8 rounded-full items-center justify-center text-xs font-semibold ${done ? "bg-petroleum text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                  <span className={`text-xs ${done ? "text-petroleum font-semibold" : "text-slate-500"}`}>{stageLabel(t, value)}</span>
                </button>
                {i < STAGE_VALUES.length - 1 && <div className={`h-0.5 w-8 ${done ? "bg-petroleum" : "bg-slate-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Left column: NDA + Chat + Agreement */}
        <div className="space-y-6">
          {!allSignedNDA && (
            <div className="kpi-card p-6 border-amber-200 bg-amber-50">
              <p className="eyebrow text-amber-700 flex items-center gap-2"><ShieldCheck size={14} /> {t("room.nda_sign_title")}</p>
              <p className="text-sm text-amber-900 mt-2">{t("room.nda_sign_desc")}</p>
              <details className="mt-3 text-xs text-slate-700 bg-white rounded-lg p-3 border border-amber-200">
                <summary className="cursor-pointer text-petroleum">{t("room.nda_view_text")}</summary>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-slate-600">{room.nda?.text}</pre>
              </details>
              {iSigned ? (
                <p className="mt-4 text-sm text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={14} /> {t("room.nda_already_signed")}</p>
              ) : (
                <div className="mt-4 flex flex-col md:flex-row gap-3">
                  <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t("room.nda_type_name_placeholder")} data-testid="input-nda-name" />
                  <Button onClick={signNda} data-testid="button-sign-nda" className="bg-petroleum text-white hover:bg-petroleum/90">
                    <PenTool size={14} className="mr-2" /> {t("room.nda_sign_button")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {allSignedNDA && (
            <div className="kpi-card p-3 border-emerald-200 bg-emerald-50 flex items-center gap-3">
              <ShieldCheck size={18} className="text-emerald-700" />
              <p className="text-sm text-emerald-800">{t("room.nda_signed_both", { date: new Date(room.nda.signatures[0].signed_at).toLocaleDateString(dateLocale) })}</p>
            </div>
          )}

          {allSignedNDA && (
            <div className="kpi-card p-5" data-testid="video-call-card">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="eyebrow text-gold">{t("room.video_eyebrow")}</p>
                  <h3 className="section-title mt-1">{t("room.video_room_label", { code: room.id.slice(0, 8) })}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {t("room.video_desc")}
                  </p>
                </div>
                <Button
                  onClick={() => setVideoOpen((v) => !v)}
                  data-testid="button-toggle-video"
                  className={videoOpen ? "bg-red-600 text-white hover:bg-red-700" : "bg-petroleum text-white hover:bg-petroleum/90"}
                >
                  {videoOpen ? <><VideoOff size={14} className="mr-2" /> {t("room.video_close")}</> : <><Video size={14} className="mr-2" /> {t("room.video_start")}</>}
                </Button>
              </div>
              {videoOpen && (
                <div className="mt-4 rounded-xl overflow-hidden border border-slate-200" data-testid="video-call-frame">
                  <iframe
                    src={`https://meet.jit.si/cinengage-${room.id}#config.prejoinPageEnabled=false&userInfo.displayName=%22${encodeURIComponent(user.name || "Cinengage")}%22`}
                    allow="camera; microphone; fullscreen; display-capture; autoplay"
                    className="w-full h-[520px] bg-black"
                    title={t("room.video_iframe_title")}
                  />
                  <div className="bg-slate-50 border-t border-slate-200 p-3 text-[11px] text-slate-500 flex items-center gap-2">
                    <Info size={12} className="text-gold" />
                    {t("room.video_share_hint")}
                    <code className="bg-white border border-slate-200 rounded px-2 py-0.5 select-all text-slate-700">
                      meet.jit.si/cinengage-{room.id}
                    </code>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chat */}
          <div className="kpi-card p-0 flex flex-col h-[520px]">
            <div className="border-b border-slate-200 px-4 py-3">
              <h3 className="section-title">{t("room.chat_title")}</h3>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {!allSignedNDA && (
                <div className="text-sm text-slate-500 italic text-center py-8">{t("room.chat_locked")}</div>
              )}
              {allSignedNDA && (room.messages || []).length === 0 && (
                <div className="text-sm text-slate-500 italic text-center py-8">{t("room.chat_empty")}</div>
              )}
              {(room.messages || []).map((m) => {
                const mine = m.from_user_id === user.id;
                const atts = m.attachments || [];
                return (
                  <div key={m.id} className={mine ? "flex justify-end" : "flex justify-start"}>
                    <div className={mine ? "bg-petroleum text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[70%]" : "bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-2 max-w-[70%]"}>
                      {m.body && <p className="text-sm">{m.body}</p>}
                      {atts.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {atts.map((a) => {
                            const isImg = (a.content_type || "").startsWith("image/");
                            const href = a.url?.startsWith("/api") ? apiUrl(a.url) : a.url;
                            return isImg ? (
                              <a key={a.id} href={href} target="_blank" rel="noreferrer" data-testid={`attachment-${a.id}`}>
                                <img src={href} alt={a.name} className="max-w-[240px] rounded-lg border border-white/20" />
                              </a>
                            ) : (
                              <a key={a.id} href={href} target="_blank" rel="noreferrer"
                                 className={`text-xs inline-flex items-center gap-2 rounded-lg px-2 py-1.5 ${mine ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"}`}
                                 data-testid={`attachment-${a.id}`}>
                                <FileText size={12} /> {a.name || t("room.attach_file_generic")}
                              </a>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString(dateLocale)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="border-t border-slate-200 p-3 bg-white">
              {pendingAttachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid="pending-attachments">
                  {pendingAttachments.map((a) => {
                    const isImg = (a.content_type || "").startsWith("image/");
                    return (
                      <div key={a.id} className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-2 py-1 text-xs">
                        {isImg ? <ImageIcon size={12} className="text-gold" /> : <FileText size={12} className="text-gold" />}
                        <span className="max-w-[160px] truncate">{a.name}</span>
                        <button type="button" onClick={() => removePending(a.id)} className="text-slate-500 hover:text-red-600" aria-label={t("room.attach_remove_aria", { name: a.name })}>
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-2">
                <input ref={fileInputRef} type="file" hidden multiple accept="image/*,.pdf,.mp4,.webp,.png,.jpg,.jpeg,.gif,.svg" onChange={handleFileSelect} data-testid="input-file-attach" />
                <Button type="button" variant="outline" size="icon" disabled={!allSignedNDA || uploading} onClick={() => fileInputRef.current?.click()} data-testid="button-attach" className="border-slate-300 shrink-0">
                  <Paperclip size={14} />
                </Button>
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={uploading ? t("room.chat_uploading") : t("room.chat_write_placeholder")} disabled={!allSignedNDA} data-testid="input-room-message" />
                <Button type="submit" disabled={!allSignedNDA} data-testid="button-room-send" className="bg-petroleum text-white hover:bg-petroleum/90">
                  <Send size={14} />
                </Button>
              </div>
            </form>
          </div>

          {/* Agreement */}
          <div className="kpi-card p-6">
            <div className="flex items-center justify-between">
              <h3 className="section-title flex items-center gap-2"><Handshake size={16} className="text-gold" /> {t("room.agreement_title")}</h3>
              <div className="flex items-center gap-2">
                {room.agreement?.status === "signed" && <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">{t("room.agreement_signed_all")}</Badge>}
                {room.agreement?.status === "pending" && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{t("room.agreement_pending")}</Badge>}
                {room.agreement?.status === "signed" && (
                  <a
                    href={apiUrl(`/api/rooms/${id}/agreement/certificate.pdf`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="button-download-agreement-pdf"
                  >
                    <Button variant="outline" size="sm" className="border-slate-300 h-8">
                      <Download size={12} className="mr-1" /> {t("room.agreement_pdf")}
                    </Button>
                  </a>
                )}
              </div>
            </div>

            {!room.agreement ? (
              <>
                <p className="text-sm text-slate-500 mt-3">{t("room.agreement_desc")}</p>
                {allSignedNDA && (
                  <div className="mt-4 space-y-3">
                    <Textarea rows={6} value={agreementText} onChange={(e) => setAgreementText(e.target.value)} placeholder={t("room.agreement_textarea_placeholder")} data-testid="textarea-agreement" />
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder={t("room.agreement_amount_placeholder")} value={agreementAmount} onChange={(e) => setAgreementAmount(e.target.value)} />
                      <Button onClick={createAgreement} className="bg-petroleum text-white hover:bg-petroleum/90" data-testid="button-create-agreement">
                        {t("room.agreement_create_draft")}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-3 space-y-4">
                <div className="bg-slate-50 rounded-xl p-4 whitespace-pre-wrap text-sm text-slate-800 border border-slate-200">
                  {room.agreement.text}
                </div>
                {room.agreement.amount && <p className="text-sm text-slate-600"><DollarSign size={12} className="inline text-gold" /> {room.agreement.currency} {room.agreement.amount.toLocaleString()}</p>}
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t("room.agreement_signatures_title")}</p>
                  {(room.agreement.signatures || []).length === 0 && <p className="text-sm text-slate-500">{t("room.agreement_no_signatures")}</p>}
                  {(room.agreement.signatures || []).map((s, i) => (
                    <div key={i} className="text-sm text-slate-700 flex items-center gap-2 border-b border-slate-100 py-2 last:border-0">
                      <PenTool size={12} className="text-gold" />
                      <span className="font-semibold">{s.typed_name}</span>
                      <span className="text-slate-500 text-xs">{s.user_name} — {new Date(s.signed_at).toLocaleString(dateLocale)}</span>
                    </div>
                  ))}
                  {!iSignedAgreement && (
                    <div className="mt-4 flex gap-3">
                      <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t("room.agreement_sign_name_placeholder")} data-testid="input-agreement-name" />
                      <Button onClick={signAgreement} data-testid="button-sign-agreement" className="bg-gold text-petroleum hover:bg-gold/90">
                        <PenTool size={14} className="mr-2" /> {t("room.agreement_sign_button")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right column: Info + Milestones */}
        <aside className="space-y-6">
          {room.project && (
            <div className="kpi-card p-5">
              <p className="eyebrow text-gold mb-2">{t("room.project_eyebrow")}</p>
              <Link to={`/proyecto/${room.project.id}`} className="text-petroleum hover:text-gold font-display text-lg block">{room.project.title}</Link>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{room.project.logline}</p>
            </div>
          )}
          {room.brief && (
            <div className="kpi-card p-5">
              <p className="eyebrow text-gold mb-2">{t("room.brief_eyebrow")}</p>
              <Link to={`/brief/${room.brief.id}`} className="text-petroleum hover:text-gold font-display text-lg block">{room.brief.title}</Link>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{room.brief.description}</p>
            </div>
          )}

          <div className="kpi-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="eyebrow text-gold">{t("room.milestones_eyebrow")}</p>
              <Dialog open={msDialog} onOpenChange={setMsDialog}>
                <DialogTrigger asChild>
                  <button className="text-xs text-petroleum hover:text-gold inline-flex items-center gap-1" data-testid="button-add-milestone">
                    <Plus size={12} /> {t("room.milestone_new")}
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("room.milestone_new_title")}</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div><Label>{t("room.milestone_title_label")}</Label><Input value={ms.title} onChange={(e) => setMs({ ...ms, title: e.target.value })} data-testid="input-milestone-title" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>{t("room.milestone_date_label")}</Label><Input type="date" value={ms.due_date} onChange={(e) => setMs({ ...ms, due_date: e.target.value })} /></div>
                      <div><Label>{t("room.milestone_amount_label")}</Label><Input type="number" value={ms.amount} onChange={(e) => setMs({ ...ms, amount: e.target.value })} /></div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setMsDialog(false)}>{t("common.cancel")}</Button>
                    <Button onClick={addMilestone} data-testid="button-save-milestone" className="bg-petroleum text-white hover:bg-petroleum/90">{t("common.save")}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            {(room.milestones || []).length === 0 ? (
              <p className="text-sm text-slate-500">{t("room.milestones_empty")}</p>
            ) : (
              <ul className="space-y-3">
                {room.milestones.map((m) => {
                  const isPaid = m.status === "paid";
                  const isDone = m.status === "completed" || isPaid;
                  return (
                    <li key={m.id} className="flex items-start gap-3">
                      <button onClick={() => !isDone && completeMilestone(m.id)} data-testid={`milestone-${m.id}`}>
                        {isDone ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-slate-400 hover:text-petroleum" />}
                      </button>
                      <div className="flex-1">
                        <p className={`text-sm ${isDone ? "line-through text-slate-400" : "text-slate-700"}`}>{m.title}</p>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-0.5 items-center">
                          {m.due_date && <span>{new Date(m.due_date).toLocaleDateString(dateLocale)}</span>}
                          {m.amount && <span className="text-gold">{m.currency} {m.amount.toLocaleString()}</span>}
                          {isPaid && <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px]">{t("room.milestone_paid_badge")}</Badge>}
                        </div>
                        {m.amount && !isPaid && (
                          <Button
                            size="sm"
                            onClick={() => payMilestone(m.id)}
                            disabled={payingMsId === m.id}
                            data-testid={`button-pay-milestone-${m.id}`}
                            className="mt-2 h-7 text-xs bg-gold text-petroleum hover:bg-gold/90"
                          >
                            <CreditCard size={12} className="mr-1" />
                            {payingMsId === m.id ? t("room.milestone_redirecting") : t("room.milestone_pay")}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="kpi-card p-4 bg-slate-50 border-slate-200">
            <p className="text-xs text-slate-500 flex items-start gap-2">
              <Info size={12} className="mt-0.5 shrink-0" />
              {t("room.signature_disclaimer")}
            </p>
          </div>

          <div className="kpi-card p-5" data-testid="share-vitrina-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="eyebrow text-gold flex items-center gap-2"><Globe size={12} /> {t("room.vitrina_eyebrow")}</p>
                <p className="text-sm text-slate-700 mt-2 font-semibold">{t("room.vitrina_toggle_title")}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <Trans
                    i18nKey="room.vitrina_toggle_desc"
                    components={[
                      <Link key="0" to="/vitrina" className="text-petroleum hover:text-gold underline" />,
                    ]}
                  />
                </p>
              </div>
              <Switch
                checked={!!room.share_publicly}
                onCheckedChange={toggleShare}
                data-testid="switch-share-publicly"
              />
            </div>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}
